import {
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { MessageService } from 'primeng/api';
import {
  SocialAuthServiceConfig,
  GoogleLoginProvider,
  SocialAuthService,
} from '@abacritt/angularx-social-login';
import { definePreset } from '@primeuix/themes';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth-interceptor';
import { PostHogErrorHandler } from './core/services/posthog-error-handler.service';
import { environment } from 'src/environments/environment';

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: 'oklch(0.98 0.02 16.5)',
      100: 'oklch(0.95 0.05 16.5)',
      200: 'oklch(0.90 0.08 16.5)',
      300: 'oklch(0.80 0.12 16.5)',
      400: 'oklch(0.70 0.18 16.5)',
      500: 'oklch(0.64 0.22 16.5)',
      600: 'oklch(0.55 0.22 16.5)',
      700: 'oklch(0.46 0.20 16.5)',
      800: 'oklch(0.38 0.16 16.5)',
      900: 'oklch(0.30 0.10 16.5)',
      950: 'oklch(0.20 0.06 16.5)',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
        },
        highlight: {
          background: '{primary.500}',
          focusBackground: '{primary.700}',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
      },
      dark: {
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.400}',
          activeColor: '{primary.300}',
        },
        highlight: {
          background: '{primary.500}',
          focusBackground: '{primary.300}',
          color: '{primary.50}',
          focusColor: '{primary.950}',
        },
      },
    },
  },
  components: {
    divider: {
      root: {
        borderColor: 'var(--muted)',
      },
    },
  },
});

registerLocaleData(localePt);

export const appConfig: ApplicationConfig = {
  providers: [
    MessageService,
    { provide: ErrorHandler, useExisting: PostHogErrorHandler },
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withPreloading(PreloadAllModules),
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimationsAsync(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    SocialAuthService,
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(environment.googleClientId, {
              oneTapEnabled: false,
            }),
          },
        ],
        onError: (err) => {
          console.error(err);
        },
      } as SocialAuthServiceConfig,
    },
  ],
};
